

'use client';

import Link from 'next/link';
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { MapPin, ArrowRight, Lightbulb, LocateFixed, Loader, HelpCircle } from 'lucide-react';
import { useState, useEffect, useRef } from 'react';
import { useToast } from '@/hooks/use-toast';
import { Badge } from '@/components/ui/badge';
import { useRouter } from 'next/navigation';
import { getPosts, type Post } from '../lib/posts';

const provinces = [
  { name: 'Antwerpen', href: '/hondenkapsalons-in-antwerpen', active: true },
  { name: 'Limburg', href: '/hondenkapsalons-in-limburg', active: true },
  { name: 'Oost-Vlaanderen', href: '/hondenkapsalons-in-oost-vlaanderen', active: true },
  { name: 'Vlaams-Brabant', href: '/hondenkapsalons-in-vlaams-brabant', active: true },
  { name: 'West-Vlaanderen', href: '/hondenkapsalons-in-west-vlaanderen', active: true },
];

// Simplified boundaries for Flemish provinces for demonstration
const provinceBoundaries: { [key: string]: { lat: [number, number], lon: [number, number] } } = {
  'West-Vlaanderen': { lat: [50.7, 51.4], lon: [2.5, 3.5] },
  'Oost-Vlaanderen': { lat: [50.7, 51.3], lon: [3.3, 4.3] },
  'Antwerpen': { lat: [51.0, 51.5], lon: [4.0, 5.2] },
  'Vlaams-Brabant': { lat: [50.7, 51.1], lon: [4.0, 5.0] },
  'Limburg': { lat: [50.7, 51.3], lon: [5.0, 5.9] },
};

function RandomBlogQuestion() {
  const [randomPost, setRandomPost] = useState<Post | null>(null);

  useEffect(() => {
    async function loadRandomPost() {
      const allPosts = await getPosts();
      const dogPosts = allPosts.filter(p => p.category === 'Hondenkapsalons');
      if (dogPosts.length > 0) {
        const randomIndex = Math.floor(Math.random() * dogPosts.length);
        setRandomPost(dogPosts[randomIndex]);
      }
    }
    loadRandomPost();
  }, []);

  if (!randomPost) {
    return null;
  }

  return (
    <Card className="max-w-2xl mx-auto group overflow-hidden flex flex-col bg-primary/10 border-primary/20 items-center justify-center text-center p-4">
        <Link href={`/blog/${randomPost.id}`} className="w-full">
            <CardHeader className="p-2">
                <HelpCircle className="mx-auto h-6 w-6 text-primary mb-2" />
                <CardTitle className="text-foreground text-lg">Wist je dat?</CardTitle>
                <p className="text-muted-foreground text-sm mt-1">
                    Een veelgestelde vraag beantwoord:
                </p>
            </CardHeader>
            <CardContent className="w-full p-2">
                <p className="font-semibold text-primary group-hover:underline">
                    {randomPost.title}
                </p>
            </CardContent>
            <CardFooter className="p-2 justify-center">
                 <p className="text-xs text-muted-foreground">Lees meer op ons blog &rarr;</p>
            </CardFooter>
        </Link>
    </Card>
  );
}


export default function ProvinceSelectionPage() {
  const [suggestedProvince, setSuggestedProvince] = useState<string | null>(null);
  const [detectedLocation, setDetectedLocation] = useState<string | null>(null);
  const [isLocating, setIsLocating] = useState(false);
  const { toast } = useToast();
  const router = useRouter();
  const provinceRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});

  useEffect(() => {
    if (suggestedProvince && provinceRefs.current[suggestedProvince]) {
      provinceRefs.current[suggestedProvince]?.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });
    }
  }, [suggestedProvince]);


  const handleLocate = () => {
    if (!navigator.geolocation) {
      toast({
        variant: "destructive",
        title: "Locatie niet ondersteund",
        description: "Uw browser ondersteunt geen geolocatie.",
      });
      return;
    }
    
    setIsLocating(true);
    setSuggestedProvince(null);
    setDetectedLocation(null);
    
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        let foundProvince: string | null = null;
        
        for (const province in provinceBoundaries) {
          const bounds = provinceBoundaries[province];
          if (
            latitude >= bounds.lat[0] && latitude <= bounds.lat[1] &&
            longitude >= bounds.lon[0] && longitude <= bounds.lon[1]
          ) {
            foundProvince = province;
            break;
          }
        }
        
        // Fetch city name from coordinates
        try {
          const response = await fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&accept-language=nl`);
          const data = await response.json();
          const city = data.address.city || data.address.town || data.address.village || data.address.suburb;
          if (city) {
            setDetectedLocation(city);
          }
        } catch (e) {
          console.error("Could not fetch city from location", e);
        }

        setIsLocating(false);
        if (foundProvince) {
          setSuggestedProvince(foundProvince);
          toast({
            title: "Suggestie gevonden!",
            description: `We denken dat u in ${foundProvince} bent.`,
          });
        } else {
          toast({
            variant: "destructive",
            title: "Provincie niet herkend",
            description: "We konden uw locatie niet koppelen aan een Vlaamse provincie.",
          });
        }
      },
      (error) => {
        setIsLocating(false);
        setDetectedLocation(null);
        let message = "We konden uw locatie niet bepalen. Controleer uw browserinstellingen.";
        if (error.code === error.PERMISSION_DENIED) {
          message = "Toegang tot locatie geweigerd. U kunt dit in uw browserinstellingen aanpassen.";
        }
        toast({
          variant: "destructive",
          title: "Locatie fout",
          description: message,
        });
      }
    );
  };
  
  const handleProvinceClick = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    if (href === '#') {
      e.preventDefault();
      return;
    }
    
    if (detectedLocation) {
        e.preventDefault();
        router.push(`${href}?location=${encodeURIComponent(detectedLocation)}`);
    }
  };

  return (
    <div className="bg-muted min-h-screen">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center mb-10">
          <MapPin className="mx-auto h-12 w-12 text-primary mb-4" />
          <h1 className="text-4xl md:text-5xl font-extrabold tracking-tight text-foreground">
            Hondenkapsalons in Vlaanderen
          </h1>
          <p className="mt-4 max-w-2xl mx-auto text-lg text-muted-foreground">
            Kies uw provincie of gebruik uw locatie om direct een overzicht van trimsalons in uw buurt te vinden.
          </p>
        </div>

        <Card className="max-w-md mx-auto shadow-lg">
            <CardContent className="p-6">
                 <Button className="w-full" size="lg" onClick={handleLocate} disabled={isLocating}>
                    {isLocating ? <Loader className="mr-2 animate-spin" /> : <LocateFixed className="mr-2" />}
                    Vind salons in mijn buurt
                </Button>
            </CardContent>
        </Card>
        
        <div className="mt-10 mb-10">
          <RandomBlogQuestion />
        </div>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
          {provinces.map((province) => {
            const isSuggested = province.name === suggestedProvince;
            return (
              <div key={province.name} ref={el => (provinceRefs.current[province.name] = el)} className="relative">
                 {isSuggested && (
                  <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 z-10 bg-primary">Suggestie</Badge>
                )}
                <div className={isSuggested ? 'animate-solar-wind' : ''}>
                  <Card 
                    className={`group h-full flex flex-col ${!province.active ? 'bg-muted/60' : 'hover:border-primary transition-colors'}`}
                  >
                    <CardHeader>
                        <CardTitle className="text-2xl">{province.name}</CardTitle>
                    </CardHeader>
                    <CardContent className="flex-grow flex flex-col justify-end">
                        {province.active ? (
                            <Button asChild className="w-full mt-4">
                                <Link href={province.href} onClick={(e) => handleProvinceClick(e, province.href)}>
                                    Bekijk salons <ArrowRight className="ml-2 h-4 w-4" />
                                </Link>
                            </Button>
                        ) : (
                            <Button className="w-full mt-4" disabled>
                                Binnenkort beschikbaar
                            </Button>
                        )}
                    </CardContent>
                  </Card>
                </div>
              </div>
            );
          })}
        </div>
        
        <div className="max-w-4xl mx-auto mb-12">
            <Card className="shadow-lg">
                <CardHeader>
                    <CardTitle className="text-3xl">Hondentrimsalon Gids België</CardTitle>
                    <p className="text-muted-foreground pt-2">Alles wat je moet weten voor en na een bezoek aan een hondentrimsalon</p>
                </CardHeader>
                <CardContent className="prose prose-sm dark:prose-invert max-w-none text-foreground/90">
                    <p>Een bezoek aan een hondentrimsalon is voor veel hondeneigenaars een terugkerend onderdeel van de verzorging, maar tegelijk ook een bron van vragen. Wanneer is trimmen nodig? Wat gebeurt er precies tijdens zo’n trimbeurt? En hoe kies je het juiste trimsalon voor jouw hond? Deze website helpt hondeneigenaars in België om weloverwogen keuzes te maken, met duidelijke informatie en een overzicht van erkende hondentrimsalons.</p>
                </CardContent>
            </Card>
        </div>

      </div>
    </div>
  );
}

    
